import { onSubmit } from '../../../app/components/login-button/components/login-form/duck';
import { isLocalEnv } from '../../../app/duck';
import styles from '../../AdoptionLogin.module.css';
import { useQuery } from '@apollo/client';
import { Button, Flex, GridItem, Image, Text } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import { FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  FormSelect,
  Icon,
  PasswordInput,
  RequiredNotification,
  TextInput,
} from '@lon/shared/components';
import { isDevMode } from '@lon/shared/constants';
import { PublicAdoptionEvent } from '@lon/shared/gql/types';
import { GqlResponse } from '@lon/shared/types';
import { getLastDistrictBySignature } from '@lon/shared/utils';
import { FormValues, GET_ADOPTION_ACCOUNTS, getValidation } from './duck';

const Form: FC = () => {
  const { t } = useTranslation();
  const { validationSchema, defaultValues } = getValidation();

  const formProviderProps = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: defaultValues,
    mode: 'onSubmit',
  });
  const { eventCode } = useParams();

  const { data: accounts, loading } = useQuery<
    GqlResponse<PublicAdoptionEvent, 'publicAdoptionEvent'>
  >(GET_ADOPTION_ACCOUNTS, {
    variables: {
      urlCode: eventCode,
    },
  });

  const districtOptions = useMemo(
    () =>
      accounts?.publicAdoptionEvent?.adoptionAccounts?.map((item) => ({
        label: `${item.name} (Adoption Account)`,
        value: item.districtId,
      })) || [],
    [accounts]
  );

  const district = formProviderProps.watch('district');

  const handleReset = () => {
    formProviderProps.resetField('district');
    formProviderProps.resetField('username');
    formProviderProps.resetField('password');
  };

  const handleSubmit = (values: FormValues) =>
    onSubmit({
      ...values,
      eventCode,
      onSuccess: () => {
        window.location.href = isDevMode()
          ? `https://local.stem.dev.stemscopes-v4-dev.aws.acceleratelearning.com:4206/mathnation`
          : '/mathnation';
      },
      sendLoginPath: true,
    });

  useEffect(() => {
    const lastDistrictBySignature = getLastDistrictBySignature();
    const isDistrictExists = districtOptions.some(
      (option) => option.value === lastDistrictBySignature?.value
    );

    if (isDistrictExists) {
      formProviderProps.setValue('district', lastDistrictBySignature);
    }
  }, [districtOptions]);

  return (
    <FormProvider {...formProviderProps}>
      <GridItem
        as="form"
        onSubmit={formProviderProps.handleSubmit(handleSubmit)}
        noValidate
        gridArea="form"
        zIndex={1}
        h="100%"
        w="100%"
        background="linear-gradient(319deg, rgba(0, 145, 200, 0.80) -5.92%, rgba(1, 82, 134, 0.80) 69.31%)"
        display="flex"
        justifyContent="center"
        alignItems="center"
        py={{
          b1024: 16,
          b375: 10,
          base: '48px',
        }}
        px={{
          b1920: '100px',
          b1440: '64px',
          b744: '48px',
          base: 6,
        }}
        sx={{
          '& abbr': {
            color: '#FFB05F',
            fontSize: '1.125rem',
            fontWeight: 'normal',
            _after: {
              right: '0.875em',
            },
          },
          '& .chakra-form__error-message': {
            color: '#FFB05F',
          },
        }}
      >
        <Flex
          flexDirection="column"
          w="100%"
          maxW="600px"
          gap={{
            b1024: 16,
            b768: 10,
            base: 6,
          }}
        >
          <Flex gap={4}>
            <Image
              src={`${isLocalEnv() ? '/' : 'login/'}assets/images/logo-mn.svg`}
              alignSelf="flex-start"
              width={{ b1440: '150px', b744: '100px', base: '80px' }}
              height={{ b1440: '128px', b744: '85px', base: '68px' }}
            />
            <Text
              color="white"
              fontWeight={400}
              display={{ b744: 'none', base: 'block' }}
              fontSize={{ b375: '0.9375rem', base: '0.8125rem' }}
              lineHeight={{ b375: '1.4rem', base: '1.2185rem' }}
            >
              A POWERFUL,
              <br />
              INTERACTIVE MATH TOOL
            </Text>
          </Flex>
          <Flex direction="column" gap={4}>
            <RequiredNotification
              color="#B7C5DE"
              markColor="#FFB05F"
              fontSize="0.875rem"
              lineHeight="1.375rem"
              fontWeight={400}
            />
            <FormSelect
              isRequired
              isLoading={loading}
              className={clsx(styles.select)}
              name="district"
              label={t('login.districtLabel')}
              aria-label={t('login.districtLabel')}
              placeholder={t('login.adoptionDistrictPlaceholder')}
              formLabelProps={{
                fontWeight: 700,
              }}
              options={districtOptions}
            />
            <Flex
              direction={{ base: 'column', md: 'row' }}
              gap={{ base: '4', md: '6' }}
              sx={{
                '[aria-label="Show/Hide password"]': {
                  color: '#8896AC',
                  _hover: {
                    backgroundColor: '#D9E3F0FF',
                  },
                },
              }}
            >
              <TextInput
                className={clsx(styles.input)}
                isRequired
                variant="dark"
                autoComplete="username"
                errorPosition="absolute"
                name="username"
                placeholder={t('login.usernamePlaceholder')}
                label={
                  <Text as="span" fontWeight={700}>
                    {t('login.usernameLabel')}
                  </Text>
                }
              />
              <PasswordInput
                className={clsx(styles.input)}
                isRequired
                errorPosition="absolute"
                variant="dark"
                name="password"
                autoComplete="current-password"
                label={
                  <Text as="span" fontWeight={700}>
                    {t('login.passwordLabel')}
                  </Text>
                }
                placeholder={t('login.passwordPlaceholder')}
              />
            </Flex>
          </Flex>
          <Flex justifyContent="space-between">
            <Button
              className={clsx(styles.button)}
              variant="outline"
              isDisabled={!district}
              onClick={handleReset}
              color="white"
              _hover={{
                color: '#1E4176',
                backgroundColor: '#B7C5DEFF',
                borderColor: '#B7C5DEFF',
              }}
              _disabled={{
                color: '#A0AEC2',
                borderColor: '#A0AEC2',
                cursor: 'not-allowed',
                _hover: {
                  backgroundColor: 'inherit',
                },
              }}
            >
              {t('login.resetButton')}
            </Button>
            <Button
              className={clsx(styles.button)}
              type="submit"
              variant="solid"
              isLoading={formProviderProps.formState.isSubmitting}
              isDisabled={!district}
              rightIcon={<Icon name="login-outlined" />}
              backgroundColor="#004875"
              color="white"
              _hover={{
                backgroundColor: '#002943',
              }}
              _active={{
                backgroundColor: '#001B2B',
              }}
              _disabled={{
                color: '#F1F5FB',
                cursor: 'not-allowed',
                backgroundColor: '#A0AEC2',
                _hover: {
                  backgroundColor: '#A0AEC2',
                },
              }}
            >
              {t('login.loginButton')}
            </Button>
          </Flex>
        </Flex>
      </GridItem>
    </FormProvider>
  );
};

export default Form;
